import { useEffect, useRef } from 'react';

/**
 * Declaratively poll while `active` is true — and only while the browser tab
 * is visible.
 *
 * Invokes `callback` every `intervalMs` for as long as `active` stays true,
 * pausing automatically when the tab is hidden (Page Visibility API) and
 * resuming when it becomes visible again. Stops for good when `active` flips
 * to false, when the callback returns (or resolves to) `false`, or on unmount.
 *
 * The callback is held in a ref, so changing its identity between renders does
 * NOT restart the timer — only `active`/`intervalMs` do.
 *
 * Replaces the hand-rolled `useRef`/`setInterval`/`clearInterval` pattern that
 * was duplicated across the AI-status components (ProposalAIReview,
 * KickoffAIPlanner, ResumeScoringSection, AIScoreCard, BidAnalysisAI). The
 * tab-visibility pause means a backgrounded opportunity tab no longer hammers
 * the backend on a timer.
 */
export function usePolling(
  callback: () => void | boolean | Promise<void | boolean>,
  intervalMs: number,
  active: boolean,
): void {
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let done = false;
    let id: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (id != null) {
        clearInterval(id);
        id = null;
      }
    };

    const tick = async () => {
      const result = await savedCallback.current();
      if (cancelled) return;
      if (result === false) {
        done = true;
        stop();
      }
    };

    const start = () => {
      if (id == null && !done && !document.hidden) {
        id = setInterval(tick, intervalMs);
      }
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, intervalMs]);
}

export default usePolling;
